/* ---------------------------------------------------------------------
   The question above a sub-category listing, answered on the listing.

   Every C2 page carries one (sections/main-plp.liquid, the `quiz` block):
   "What's your go-to fabric?" on Topwear and Bottomwear, "What's your
   shoe vibe?" on Footwear, "What are you packing for?" on Bags & Luggage,
   and so on down the template. Until now those pills opened the site-wide
   quiz modal — the shopper answered a question about this shelf and the
   shelf stayed exactly as it was. This answers it where it was asked: the
   grid below rebuilds to the products that suit the answer.

   It does not own the grid. assets/vro-plp-shopby.js already rebuilds the
   listing for the Shop-by rows, so two files writing cards into the same
   container would draw over each other. Instead this registers a filter
   with that file (window.VroPlpShopBy.setNarrow) and it runs on every
   rebuild, which is what makes the two compose:

     /collections/topwear?shop=category:t-shirts  +  "Cotton"
       -> the t-shirts shelf, narrowed to the cotton ones

   Tapping another category afterwards re-asks the question of the new
   shelf; Retake drops the answer and the listing goes back to what the
   rows alone said.

   How a product is judged to suit an answer
   -----------------------------------------
   From the store's own fields, never from prose. Each product in the
   shelf is read out of /collections/<handle>?view=vro-personalize — the
   same pool the home-page quizzes rank — which prints, per product, its
   type, tags, title, and the structured metafields the merchant fills in
   (VRO Needs, Key Benefits, Concerns, Fit, Ingredients, Fragrance family,
   Occasion, Season …). An answer scores against those, weighted by where
   it matched:

     product type      4   "Sneakers" IS the type of the product
     title             3   "… Cotton T-Shirt"
     structured fields 2   key_benefits says "Anti-Dandruff"
     tags              1   weakest — a tag is often the whole shelf's

   A tag-only match is dropped while anything better exists, which is what
   keeps "Long Trip" from returning all 230 bags because every one of them
   is tagged "bags & luggage". Products are shown best-first.

   When nothing matches, the listing is left alone and the bar says so
   rather than emptying the page: "Modal" has no products on this store
   today, and a shopper who asked for it is better served by the shelf
   plus an honest line than by a blank grid.
--------------------------------------------------------------------- */
(function () {
  'use strict';

  var FACET_PAGES = 6;          /* 250 products a page — the biggest shelf here is 669 */
  var POOL_PAGES = 14;          /* the fallback's pages, at 50 a page */
  var MIN_KEEP = 1;             /* below this, the answer is treated as unmatched */
  /* Below this many strong matches, the weaker ones are let back in: a
     listing of three is a worse answer than a listing of twelve, two of
     which are arguable. */
  var ENOUGH = 8;

  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------
     What an answer means, where the words alone don't carry it.

     Most options say what they mean: "Cotton" is the word that appears
     on a cotton shirt, "Creatine" on a tub of creatine. These are the
     ones where the shopper's word and the catalogue's word differ —
     the shopper picks "Self-winding", the watch says "Automatic". Keyed
     by the option label, lowercased; anything not listed falls back to
     the words of the label itself.

     Adding a new quiz to a template needs no edit here unless its
     options are in that second category.
  --------------------------------------------------------------- */
  var MEANS = {
    /* Fashion — fabric */
    'viscose rayon': ['viscose', 'rayon'],
    'modal': ['modal'],
    /* Footwear */
    'sneakers': ['sneaker', 'trainer', 'casual shoe'],
    'loafers': ['loafer', 'moccasin'],
    'boots': ['boot'],
    'sports': ['sports shoe', 'running shoe', 'training shoe', 'athletic', 'sports'],
    'flip flops': ['flip flop', 'slipper', 'thong'],
    /* Bags & luggage */
    'college': ['backpack', 'college', 'school', 'daypack'],
    'office': ['laptop', 'office', 'briefcase', 'messenger', 'formal'],
    'casual outing': ['sling', 'crossbody', 'cross body', 'tote', 'mini', 'casual'],
    'long trip': ['trolley', 'suitcase', 'duffle', 'duffel', 'cabin', 'check-in'],
    /* Watches */
    'self-winding': ['self winding', 'self-winding', 'automatic', 'mechanical'],
    'chronograph': ['chronograph', 'chrono'],
    'smart watch': ['smartwatch', 'smart watch'],
    /* Accessories */
    'belts': ['belt'],
    'wallets': ['wallet', 'card holder', 'cardholder'],
    'sunglasses': ['sunglass', 'eyewear', 'shades'],
    'cufflinks': ['cufflink', 'cuff link'],
    'pocketsquare': ['pocket square', 'pocketsquare'],
    /* Nutrition */
    'energy': ['energy', 'pre workout', 'pre-workout', 'caffeine'],
    'immunity': ['immunity', 'immune', 'vitamin c', 'zinc'],
    'recovery': ['recovery', 'muscle recovery', 'repair', 'bcaa'],
    'daily wellness': ['daily wellness', 'multivitamin', 'daily'],
    'protein': ['protein', 'whey', 'isolate'],
    'electrolyte': ['electrolyte', 'hydration', 'hydrate'],
    'amino acids': ['amino', 'bcaa', 'eaa'],
    'stamina': ['stamina', 'endurance', 'long lasting'],
    'vitality': ['vitality', 'testosterone', 'virility'],
    'contraceptions': ['condom', 'contraceptive', 'contraception'],
    /* Face */
    'hydration': ['hydration', 'hydrating', 'moisturis', 'moisturiz', 'hyaluronic'],
    'brightening': ['brighten', 'glow', 'radiance', 'vitamin c'],
    'anti-tanning': ['anti tan', 'anti-tan', 'de tan', 'de-tan', 'detan', 'tan removal'],
    'acne clear': ['acne', 'pimple', 'blemish', 'salicylic', 'breakout'],
    /* Hair */
    'volume': ['volume', 'volumising', 'volumizing', 'thickening'],
    'smoothness': ['smooth', 'frizz', 'silky', 'straighten'],
    'strengthen': ['strengthen', 'strength', 'breakage', 'hair fall'],
    'dandruff free': ['dandruff', 'anti-dandruff', 'flake'],
    'grow my hair': ['hair growth', 'regrowth', 'growth', 'hair fall'],
    /* Fragrance */
    'fresh': ['fresh', 'citrus', 'aquatic', 'marine'],
    'woody': ['woody', 'wood', 'oud', 'sandalwood', 'cedar', 'vetiver'],
    'spicy': ['spicy', 'spice', 'oriental', 'amber', 'cinnamon'],
    'classy': ['classic', 'elegant', 'luxe', 'signature'],
    /* Beard */
    'stubble': ['stubble', 'short beard', 'trimmer'],
    'sharp': ['beard shaper', 'styling', 'shape', 'wax', 'define'],
    'full growth': ['beard growth', 'growth', 'beard oil', 'serum'],
    /* Performance accessories */
    'stay calm and do yoga': ['yoga', 'mat', 'meditation', 'stretch'],
    'hit the gym': ['gym', 'training', 'weight', 'lifting'],
    'go for morning run club': ['running', 'run', 'jog'],
    'swim and stay fit': ['swim', 'goggle', 'pool'],
    'cycle to work': ['cycling', 'cycle', 'bike']
  };

  /* Words that carry no meaning of their own in an option label. */
  var NOISE = ' the a an and or of for my your to in on with is are do i we you me it '.split(' ');

  var poolCache = {};
  var section, answer = null, cards = [], lastCount = 0;

  /* ---------- small helpers ---------- */

  function all(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function norm(s) {
    return String(s || '').replace(/[‘’“”]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function motion() {
    return window.VroMotion || null;
  }

  function reduced() {
    var m = motion();
    return m ? m.reduced() : false;
  }

  /* The words an answer is looked for by. */
  function meaningOf(label) {
    var key = norm(label);
    if (MEANS[key]) return MEANS[key].map(norm);
    var words = key.split(/[^a-z0-9']+/).filter(function (w) {
      return w.length > 2 && NOISE.indexOf(w) === -1;
    });
    var out = [];
    if (key.indexOf(' ') > -1) out.push(key);
    words.forEach(function (w) { if (out.indexOf(w) === -1) out.push(w); });
    return out.length ? out : [key];
  }

  /* ---------- the shelf, as data ---------- */

  /* What every product of a collection says about itself, as
     handle -> { type, title, tags, fields }.

     From templates/collection.vro-facets.liquid, which prints those four
     strings and nothing else, 250 products a page. If that view is not on
     the theme yet the request comes back as a page of HTML rather than
     JSON, and the heavier personalization pool answers instead — so this
     file works either way. */
  function shelfOf(handle) {
    if (poolCache[handle]) return poolCache[handle];

    function page(n) {
      return fetch(ROOT_URL + 'collections/' + encodeURIComponent(handle) +
                   '?view=vro-facets&page=' + n, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (text) {
          var data = JSON.parse(text);
          var out = { pages: parseInt(data.pages, 10) || 1, items: {} };
          (data.items || []).forEach(function (it) {
            out.items[it.h] = { type: it.t || '', title: it.n || '', tags: it.g || '', fields: it.s || '',
                                material: (it.m || '').replace(/^[\s|]+|[\s|]+$/g, '') };
          });
          return out;
        });
    }

    var job = page(1).then(function (first) {
      var pages = Math.min(first.pages, FACET_PAGES);
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(page(p));
      return Promise.all(rest).then(function (more) {
        var items = first.items;
        more.forEach(function (d) {
          for (var h in d.items) items[h] = d.items[h];
        });
        return items;
      });
    }).catch(function () {
      return poolOf(handle);
    });

    job.catch(function () { delete poolCache[handle]; });
    poolCache[handle] = job;
    return job;
  }

  /* The fallback: the same words, read off the personalization pool's
     finished cards. */
  function poolOf(handle) {
    function page(n) {
      return fetch(ROOT_URL + 'collections/' + encodeURIComponent(handle) +
                   '?view=vro-personalize&page=' + n, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var t = document.createElement('template');
          t.innerHTML = html;
          var meta = t.content.querySelector('[data-vro-pool]');
          var out = { pages: meta ? parseInt(meta.getAttribute('data-vro-pages'), 10) || 1 : 1, items: {} };
          all('[data-vro-handle]', t.content).forEach(function (card) {
            var signals = card.getAttribute('data-vro-signals') || '';
            out.items[card.getAttribute('data-vro-handle')] = {
              /* The pool prints the product type first in the signals
                 list, which is the strongest thing a listing can say
                 about what a product IS. */
              type: norm(signals.split('|')[0]),
              title: norm(card.getAttribute('data-vro-title')),
              tags: norm(card.getAttribute('data-vro-tags')),
              fields: norm([
                signals,
                card.getAttribute('data-vro-benefits'),
                card.getAttribute('data-vro-ingredients'),
                card.getAttribute('data-vro-fit'),
                card.getAttribute('data-vro-skin-type'),
                card.getAttribute('data-vro-hair-type')
              ].join(' | '))
            };
          });
          return out;
        });
    }

    return page(1).then(function (first) {
      var pages = Math.min(first.pages, POOL_PAGES);
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(page(p));
      return Promise.all(rest).then(function (more) {
        var items = first.items;
        more.forEach(function (d) {
          for (var h in d.items) items[h] = d.items[h];
        });
        return items;
      });
    });
  }

  /* Whole words, not letters found inside other words.

     indexOf alone let "silk" match "silky smooth", "tan" match "titanium"
     and "run" match "running shoes" on a shelf where that was not the
     question — a handful of products in every answer that plainly did not
     belong. A word counts when what sits either side of it is not a
     letter or a digit, which is also what makes "anti-dandruff" match a
     field written "anti dandruff".

     Compiled once per word: an answer asks this of every product on the
     shelf, several hundred times over. */
  var wordRe = {};

  function hasWord(hay, w) {
    if (!hay || !w) return false;
    var re = wordRe[w];
    if (!re) {
      re = new RegExp('(^|[^a-z0-9])' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                      '([^a-z0-9]|$)', 'i');
      wordRe[w] = re;
    }
    return re.test(hay);
  }

  /* Is this answer a fabric? It is when some product on the shelf names
     it in its Material field ("Linen", "Cotton", "Denim"). Then the
     Material field decides: a product that states what it is made of
     answers "Linen" only if that says linen — a cotton sweatshirt is not
     a linen one because "linen" is in a tag or the title of a range. A
     product with no Material filled in is judged by its words, as before. */
  function materialQuestion(items, words) {
    for (var h in items) {
      var m = items[h] && items[h].material;
      if (!m) continue;
      for (var i = 0; i < words.length; i++) if (hasWord(m, words[i])) return true;
    }
    return false;
  }

  /* How well one product answers one option. 0 = it doesn't. */
  function scoreOf(item, words, byMaterial) {
    if (!item) return 0;
    if (byMaterial && item.material) {
      for (var j = 0; j < words.length; j++) if (hasWord(item.material, words[j])) return 5;
      return 0;
    }
    var best = 0;
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      var here = 0;
      if (item.type && hasWord(item.type, w)) here = 4;
      else if (hasWord(item.title, w)) here = 3;
      /* Merchandising writes compound fabrics into titles — "polycotton",
         "cottonblend" — so a title still counts when the word is only
         inside another, one rung lower than a title that says it plainly.
         Titles only: a tag or a metafield that merely contains the letters
         is the noise this is here to keep out. */
      else if (item.title.indexOf(w) > -1) here = 2.5;
      else if (hasWord(item.fields, w)) here = 2;
      else if (hasWord(item.tags, w)) here = 1;
      /* The shopper's own word beats a word that merely stands in for it.
         Without this, "Sneakers" put every loafer and boot first on
         Footwear, because all of them are typed "casual shoes" and the
         one real sneaker only said so in its title.

         Not for a match that is only letters inside a longer word: a
         bonus there lifted "silky smooth" to the score of a product whose
         title says silk, which is the false match this pass is removing,
         not one to promote. */
      if (here >= 2 && here !== 2.5 && i === 0) here += 1.5;
      if (here > best) best = here;
    }
    return best;
  }

  /* ---------- the filter the listing runs through ---------- */

  /* Which products of the whole shelf this answer keeps, at the strength
     it settled on for the grid — for the filter counts. null: it keeps
     everything (no answer, or nothing matched). */
  var keptMin = 0;
  narrow.allowed = function (base) {
    if (!answer || !keptMin) return null;
    if (keptMin === Infinity) return {};   /* it kept nothing */
    var words = meaningOf(answer.label);
    var min = keptMin;
    return shelfOf(base).then(function (items) {
      var out = {};
      var byMaterial = materialQuestion(items, words);
      Object.keys(items).forEach(function (h) { if (scoreOf(items[h], words, byMaterial) >= min) out[h] = true; });
      return out;
    }, function () { return null; });
  };

  function narrow(handles, base) {
    if (!answer) return handles;
    var words = meaningOf(answer.label);
    var mine = answer;

    return shelfOf(base).then(function (items) {
      if (mine !== answer) return handles;

      var byMaterial = materialQuestion(items, words);
      var scored = handles.map(function (h, i) {
        return { h: h, i: i, s: scoreOf(items[h], words, byMaterial) };
      });

      /* Strongest answer first, widening only when it is too thin to be a
         listing.

         A product whose TYPE or TITLE names the thing asked for is
         answering the question; one that merely carries the word in a
         metafield among thirty others might be. Mixed together, an answer
         with ninety good matches still had the vague ones in it, which is
         what a shopper reads as "why is this here". So the strong set is
         the answer when there is enough of it, and the weaker tiers come
         back one at a time when there is not.

         A tag is often the whole shelf's ("bags & luggage" on every bag),
         so a tag-only match is still the last resort it always was. */
      var kept = scored.filter(function (r) { return r.s >= 3; });
      if (kept.length < ENOUGH) {
        kept = scored.filter(function (r) { return r.s >= 2; });
      }
      if (kept.length < MIN_KEEP) kept = scored.filter(function (r) { return r.s >= 1; });

      /* Nothing here answers it: say so, with an empty listing, rather
         than leave the unmatched shelf standing under "Noted — Linen",
         which read as six cotton sweatshirts being linen. The empty
         listing offers to clear the selection. */
      if (kept.length < MIN_KEEP) {
        lastCount = 0;
        keptMin = Infinity;
        said(0, handles.length);
        return [];
      }

      kept.sort(function (a, b) { return (b.s - a.s) || (a.i - b.i); });
      /* The bar above counts what this answer keeps across the whole
         shelf (assets/vro-facet-count.js), at the same strength. */
      keptMin = kept.reduce(function (m, r) { return Math.min(m, r.s); }, Infinity);
      lastCount = kept.length;
      said(kept.length, handles.length);
      return kept.map(function (r) { return r.h; });
    }, function () {
      return handles;              /* the pool didn't load: show the shelf */
    });
  }

  /* ---------- the card, before and after ---------- */

  function tick() {
    var s = document.createElement('span');
    s.className = 'vro-noted-check';
    s.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2.6" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return s;
  }

  function retakeButton() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'vro-reselect vro-reselect--noted is-visible';
    b.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg><span>Retake</span>';
    b.addEventListener('click', reset);
    return b;
  }

  /* The answered state of the card: the question's pills roll away and
     the same bar every other quiz in the theme leaves behind takes their
     place, in the card, where the question was. */
  function noted(count, total) {
    if (!answer) return;
    var card = answer.card;
    var bar = card.querySelector('[data-vro-plp-quiz-noted]');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'vro-noted-pill vro-plp-quiz-noted';
      bar.setAttribute('data-vro-plp-quiz-noted', '');
      bar.setAttribute('role', 'status');
      card.appendChild(bar);
    }

    /* The merchant's own words for this answer, when the block has them
       (sections/main-plp.liquid, "Noted text": Chip | Title | Line, printed
       on the chip as data-noted-title / data-noted-line). The title
       replaces the chip's wording after "Noted —" and the line replaces
       the product count; the count itself stays on the right. */
    var chip = null;
    pillsOf(card).forEach(function (p) {
      if (!chip && norm(p.textContent) === norm(answer.label)) chip = p;
    });
    var notedTitle = chip && chip.getAttribute('data-noted-title');
    var notedLine = chip && chip.getAttribute('data-noted-line');

    var title = notedTitle || answer.label;
    var detail;
    /* The sheet's line is final: it is shown from the moment the chip is
       picked and stays whether or not the listing found matches. Only a
       chip without a line falls back to the count / status wording. */
    if (count === 0) detail = 'Nothing in this selection matches ' + answer.label.toLowerCase() + ' yet — try another answer, or clear the selection.';
    else if (notedLine) detail = notedLine;
    else if (count === null) detail = 'Nothing in this category is tagged that yet — showing everything.';
    else if (count === undefined) detail = 'Finding what suits you…';
    else detail = count === 1 ? '1 product, best match first' : count + ' products, best match first';

    var text = document.createElement('span');
    text.className = 'vro-noted-text';
    var t = document.createElement('span');
    t.className = 'vro-noted-title';
    t.textContent = 'Noted — ' + title;
    text.appendChild(t);
    var d = document.createElement('span');
    d.className = 'vro-noted-detail';
    d.textContent = detail;
    text.appendChild(d);
    text.appendChild(retakeButton());

    /* The pair every Noted bar in the theme prints: what the answer found,
       out of the shelf it was found in. With a Shop-by pick standing the
       shelf is that sub-category, which is the listing the shopper is
       actually looking at — "6 from 290" is about the rows on screen, not
       about the catalogue. */
    var countEl = null;
    if (typeof count === 'number' && total > 0) {
      countEl = document.createElement('span');
      countEl.className = 'info-pill-count';
      countEl.textContent = count + ' from ' + total;
    }

    var fill = function () {
      bar.innerHTML = '';
      bar.appendChild(tick());
      bar.appendChild(text);
      if (countEl) bar.appendChild(countEl);
    };

    var m = motion();
    if (m && !reduced()) m.morphHeight(card, fill, { duration: 360 });
    else fill();

    card.classList.add('vro-plp-quiz-answered');
    bar.classList.add('vro-noted-enter');
  }

  function said(count, total) {
    if (!answer) return;
    noted(count, total);
  }

  function pillsOf(card) {
    return all('.quiz-teaser-pill-btn', card);
  }

  /* ---------- answering ---------- */

  function answerWith(card, label) {
    answer = { card: card, label: label };
    writeUrl();
    noted(undefined);
    if (window.VroPlpShopBy) window.VroPlpShopBy.setNarrow(narrow);
  }

  function reset() {
    if (!answer) return;
    var card = answer.card;
    answer = null;
    writeUrl();

    var back = function () {
      var bar = card.querySelector('[data-vro-plp-quiz-noted]');
      if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
      card.classList.remove('vro-plp-quiz-answered');
    };
    var m = motion();
    if (m && !reduced()) m.morphHeight(card, back, { duration: 320 });
    else back();

    if (window.VroPlpShopBy) window.VroPlpShopBy.setNarrow(null);
  }

  /* ---------- the answer in the address bar ---------- */

  function writeUrl() {
    var params = new URLSearchParams(window.location.search);
    if (answer) params.set('vroq', answer.label);
    else params.delete('vroq');
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs.replace(/%3A/gi, ':').replace(/%2C/gi, ',') : '') +
      window.location.hash;
    try { window.history.replaceState(window.history.state, '', url); } catch (e) { /* ignore */ }
  }

  function readUrl() {
    var raw = new URLSearchParams(window.location.search).get('vroq');
    if (!raw) return null;
    var want = norm(raw);
    for (var i = 0; i < cards.length; i++) {
      var hit = null;
      pillsOf(cards[i]).forEach(function (p) {
        if (norm(p.textContent) === want) hit = p;
      });
      if (hit) return { card: cards[i], label: hit.textContent.trim() };
    }
    return null;
  }

  /* ---------- wiring ---------- */

  /* The pills carry data-quiz-open, which is what sent them to the
     site-wide modal. The modal's own listener is on the document, so
     catching the click on the way down — before it gets there — is what
     keeps the answer on this page without touching assets/quiz.js. */
  function intercept(e) {
    var pill = e.target.closest && e.target.closest('.quiz-teaser-pill-btn');
    if (!pill) return;
    var card = pill.closest('.quiz-teaser-card');
    if (!card || cards.indexOf(card) === -1) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    e.stopPropagation();
    var label = pill.textContent.trim();
    if (answer && norm(answer.label) === norm(label)) reset();
    else answerWith(card, label);
  }

  function style() {
    if (document.getElementById('vro-plp-quiz-style')) return;
    var css =
      '.plp-quiz .quiz-teaser-card{ transition: height .36s cubic-bezier(.22,.9,.3,1); }' +
      /* Answered, the card IS the bar. The question, the pills and the
         sparkle go, and so does the card's own panel — its background,
         padding and border — or the bar sits inside a second box whose
         heading still asks something the shopper has already answered. */
      '.plp-quiz .quiz-teaser-card.vro-plp-quiz-answered .quiz-teaser-pill-grid,' +
      '.plp-quiz .quiz-teaser-card.vro-plp-quiz-answered .quiz-teaser-subheading,' +
      '.plp-quiz .quiz-teaser-card.vro-plp-quiz-answered .quiz-teaser-heading,' +
      '.plp-quiz .quiz-teaser-card.vro-plp-quiz-answered .quiz-teaser-diamond{ display: none !important; }' +
      '.plp-quiz .quiz-teaser-card.vro-plp-quiz-answered{' +
      '  background: none !important; border: 0 !important; box-shadow: none !important;' +
      '  padding: 0 !important; gap: 0 !important; }' +
      '.plp-quiz .vro-plp-quiz-noted{ margin: 0; width: 100%; }';
    var el = document.createElement('style');
    el.id = 'vro-plp-quiz-style';
    el.textContent = css;
    document.head.appendChild(el);
  }

  function init() {
    /* Without the listing rebuilder there is nothing to narrow, and the
       pills are better off opening the modal as they did before. */
    if (!window.VroPlpShopBy) return;
    section = document.querySelector('[data-vro-shopby]');
    if (!section) return;
    cards = all('.plp-quiz .quiz-teaser-card', section).filter(function (c) {
      return pillsOf(c).length > 0;
    });
    if (!cards.length) return;

    style();
    cards.forEach(function (c) {
      c.setAttribute('data-vro-plp-quiz', '');
      /* The card itself opens the modal on some templates; on a listing
         the pills are the whole question. */
      c.removeAttribute('data-quiz-open');
      pillsOf(c).forEach(function (p) {
        p.removeAttribute('data-quiz-open');
        p.setAttribute('aria-pressed', 'false');
      });
    });

    document.addEventListener('click', intercept, true);

    var back = readUrl();
    if (back) answerWith(back.card, back.label);
  }

  function start() {
    if (window.VroPlpShopBy) { init(); return; }
    /* This file is loaded after assets/vro-plp-shopby.js, but that one
       waits for DOMContentLoaded before it publishes itself. */
    document.addEventListener('vro:shopby:ready', init, { once: true });
    setTimeout(function () { if (!cards.length) init(); }, 1200);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
